import React, { useState, FunctionComponent, useEffect, useCallback, useRef } from 'react';
import { Presentation } from './presentation';
import type { PersonShowModalProps as Props, PersonShowModalState } from './types';
import { useFlash } from '../../../hooks/useFlash';
import { MANAGER_OCCUPATION_VALIDATION_MESSAGE } from '../../../libs/occupationValidation';

export const Component: FunctionComponent<Props> = ({
  isPc,
  handleOpenModal,
  onChangedBody,
  isModal,
  resumeLoading,
  personId,
  startYearList,
  birthdayListStart,
  endYearList,
  birthdayList,
  graduationList,
  name,
  follows,
  gender,
  location,
  birthday,
  age,
  graduationYear,
  tel,
  mail,
  instagram,
  twitter,
  introduction,
  activity,
  slogan,
  skills,
  favoriteBrands,
  interests,
  motivation,
  salary,
  salaries,
  handleUpdate,
  handleDeleteHistories,
  resume,
  job_resume,
  scouts,
  entries,
  favorites,
  favoritesCounts,
  entriesCounts,
  scoutsCounts,
  type,
  scoutId,
  scoutTitle,
  scoutCreatedAt,
  scoutMessage,
  title,
  target,
  clickTarget,
  educationalHistories,
  careerHistories,
  preferredLocations,
  preferredOccupations,
  currentOccupations,
  uncompletes,
  careerEmploymentTypes,
  preferenceConditions,
}) => {
  const commitHandlersRef = useRef<Record<string, () => any>>({});
  const validationHandlersRef = useRef<Record<string, () => boolean>>({});
  const { flash, flashType, flashMessage, handleFlash } = useFlash();

  const registerCommitHandler = useCallback((key: string, handler: () => any) => {
    commitHandlersRef.current[key] = handler;
  }, []);

  const registerValidationHandler = useCallback((key: string, handler: () => boolean) => {
    validationHandlersRef.current[key] = handler;
  }, []);

  const [state, setState] = useState<PersonShowModalState>({
    deleted: false,
    educationalHistories: educationalHistories || [],
    careerHistories: careerHistories || [],
    skills: skills || [],
    interests: interests || [],
    favoriteBrands: favoriteBrands || [],
    preferredLocations: preferredLocations || [],
    preferenceConditions: preferenceConditions || [],
    introduction: introduction || '',
    activity: activity || '',
    slogan: slogan || '',
    motivation: motivation || '',
    birthday: birthday || '',
  });

  const educationalTemplate = {
    department: '',
    description: '',
    descriptionRaw: '',
    endDate: '',
    startDate: '',
    name: '',
    id: 0,
  };

  const careerTemplate = {
    currentWorking: false,
    department: '',
    description: '',
    startDate: '',
    endDate: '',
    name: '',
    id: 0,
    position: '',
    salaryRange: 1,
  };

  useEffect(() => {
    const valueMap: { [key: string]: any } = {
      educationalHistories,
      careerHistories,
      skills,
      interests,
      favoriteBrands,
      preferredLocations,
      preferenceConditions,
      introduction,
      activity,
      slogan,
      motivation,
      birthday,
    };

    if (target && target in valueMap) {
      setState(prev => ({
        ...prev,
        [target]: valueMap[target],
      }));
    }
  }, [
    target,
    educationalHistories,
    careerHistories,
    skills,
    interests,
    favoriteBrands,
    preferredLocations,
    preferenceConditions,
    introduction,
    activity,
    slogan,
    motivation,
    birthday,
  ]);

  const handleClear = useCallback(() => {
    const newState: Partial<PersonShowModalState> = {};
    const key = target as keyof PersonShowModalState;

    if (target === 'educationalHistories') {
      const originHash = [...state.educationalHistories];
      originHash.splice(clickTarget as number, 1, educationalTemplate);
      newState.educationalHistories = originHash;
    } else if (target === 'careerHistories') {
      newState.careerHistories = [...state.careerHistories];
    } else if (
      ['interests', 'skills', 'favoriteBrands', 'preferredLocations', 'preferenceConditions'].indexOf(target) !== -1
    ) {
      newState[key] = [] as any;
      if (onChangedBody) onChangedBody(target, []);
    } else if (
      ['resume', 'job_resume'].indexOf(target) !== -1
    ) {
      newState[key] = '' as any;
      if (onChangedBody) onChangedBody(target, '');
      newState.deleted = true;
      setTimeout(() => {
        setState(prev => ({ ...prev, deleted: false }));
      }, 5000);
    } else {
      newState[key] = '' as any;
      if (onChangedBody) onChangedBody(target, '');
    }

    setState(prev => ({ ...prev, ...newState }));
  }, [clickTarget, educationalTemplate, careerTemplate, onChangedBody, state, target]);

  const onChangedText = useCallback((name: string, value: any) => {
    if (onChangedBody) onChangedBody(name, value);
  }, [onChangedBody]);

  const onHandleOpenModal = useCallback(() => {
    handleOpenModal('', '');
  }, [handleOpenModal]);

  const onHandleUpdate = useCallback(() => {
    const isValid = validationHandlersRef.current[target]?.() ?? true;
    if (!isValid) {
      handleFlash(true, 'danger', MANAGER_OCCUPATION_VALIDATION_MESSAGE);
      return;
    }

    const overrideValue = commitHandlersRef.current[target]?.();
    if (handleUpdate) handleUpdate(target, false, overrideValue);
  }, [handleFlash, handleUpdate, target]);

  const onHandleDeleteHistories = useCallback(() => {
    if (handleDeleteHistories) handleDeleteHistories();
  }, [handleDeleteHistories]);

  const isLastIndex = (index: number) => index === (uncompletes?.length ?? 0) - 1;

  return (
    <Presentation
      state={state}
      flash={{ flash, flashType, flashMessage }}
      handleFlash={handleFlash}
      isPc={isPc}
      isModal={isModal}
      resumeLoading={resumeLoading}
      personId={personId}
      startYearList={startYearList}
      birthdayListStart={birthdayListStart}
      endYearList={endYearList}
      birthdayList={birthdayList}
      graduationList={graduationList}
      name={name}
      follows={follows}
      gender={gender}
      location={location}
      birthday={birthday}
      age={age}
      graduationYear={graduationYear}
      tel={tel}
      mail={mail}
      instagram={instagram}
      twitter={twitter}
      introduction={introduction}
      activity={activity}
      slogan={slogan}
      motivation={motivation}
      salary={salary}
      salaries={salaries}
      resume={resume}
      job_resume={job_resume}
      scouts={scouts}
      entries={entries}
      favorites={favorites}
      favoritesCounts={favoritesCounts}
      entriesCounts={entriesCounts}
      scoutsCounts={scoutsCounts}
      type={type}
      scoutId={scoutId}
      scoutTitle={scoutTitle}
      scoutCreatedAt={scoutCreatedAt}
      scoutMessage={scoutMessage}
      title={title}
      target={target}
      clickTarget={clickTarget}
      preferredOccupations={preferredOccupations}
      currentOccupations={currentOccupations}
      onChangedText={onChangedText}
      onChangedBody={onChangedBody}
      handleOpenModal={handleOpenModal}
      onHandleOpenModal={onHandleOpenModal}
      handleUpdate={handleUpdate}
      onHandleUpdate={onHandleUpdate}
      handleClear={handleClear}
      handleDeleteHistories={onHandleDeleteHistories}
      registerCommitHandler={registerCommitHandler}
      registerValidationHandler={registerValidationHandler}
      uncompletes={uncompletes}
      isLastIndex={isLastIndex}
      careerEmploymentTypes={careerEmploymentTypes}
      preferenceConditions={state.preferenceConditions}
    />
  );
};
